<template>
	<div class="p-grid">
		<div class="p-col-12">
			<div class="card">
				<h4>Risoluzione Pagamenti Orfani</h4>
				<p>Elenco dei dettagli di pagamento associati a richieste cancellate.</p>

				<DataTable :value="orphans" :loading="loading" class="p-datatable-sm" responsiveLayout="scroll">
					<Column field="id_pagamento_det" header="ID Det"></Column>
					<Column field="atleta_nome" header="Atleta" :sortable="true">
                        <template #body="slotProps">
                            <a :href="'/atleta/' + slotProps.data.id_atleta + '/dettagli'" target="_blank" class="atleta-link">
                                {{ slotProps.data.atleta_nome }}
                                <i class="pi pi-external-link p-ml-1" style="font-size: 0.8rem"></i>
                            </a>
                        </template>
                    </Column>
					<Column field="importo_riga" header="Riga">
						<template #body="slotProps">
							{{ formatCurrency(slotProps.data.importo_riga) }}
						</template>
					</Column>
                    <Column field="importo_totale_pagamento" header="Totale">
						<template #body="slotProps">
							<span :class="{'text-bold': slotProps.data.importo_totale_pagamento != slotProps.data.importo_riga}">
                                {{ formatCurrency(slotProps.data.importo_totale_pagamento) }}
                            </span>
						</template>
					</Column>
                    <Column field="metodo_pagamento" header="Metodo"></Column>
                    <Column field="pagamento_nota" header="Nota Pagamento">
                        <template #body="slotProps">
                            <span v-if="slotProps.data.pagamento_nota" style="font-style: italic; color: #666;">
                                {{ slotProps.data.pagamento_nota }}
                            </span>
                            <span v-else>-</span>
                        </template>
                    </Column>
					<Column field="data_pagamento" header="Data" :sortable="true"></Column>
                    <Column field="altri_pagamenti_giorno" header="Altri pagamenti atleta (stesso giorno)">
                        <template #body="slotProps">
                            <span v-if="slotProps.data.altri_pagamenti_giorno" class="others-alert">
                                {{ slotProps.data.altri_pagamenti_giorno }}
                            </span>
                            <span v-else class="text-muted">Nessuno</span>
                        </template>
                    </Column>
					<Column field="old_request_id" header="ID Richiesta"></Column>
					<Column header="Azioni">
						<template #body="slotProps">
                            <div class="p-d-flex">
                                <Button icon="pi pi-link" class="p-button-rounded p-button-info p-mr-2" 
                                    @click="openResolveDialog(slotProps.data)" 
                                    v-tooltip.top="'Associa a nuova richiesta'" />
                                
                                <Button icon="pi pi-trash" class="p-button-rounded p-button-danger" 
                                    @click="confirmDeletePayment(slotProps.data)" 
                                    v-tooltip.top="'Elimina intero pagamento'" />
                            </div>
						</template>
					</Column>
				</DataTable>
			</div>
		</div>

		<Dialog v-model:visible="resolveDialog" header="Riassegna Pagamento" :style="{width: '600px'}" :modal="true">
			<div v-if="selectedPayment">
				<p><strong>Atleta:</strong> {{ selectedPayment.atleta_nome }}</p>
				<p><strong>Importo Pagato (questa riga):</strong> {{ formatCurrency(selectedPayment.importo_riga) }}</p>
				<p><strong>Data:</strong> {{ selectedPayment.data_pagamento }}</p>
				
				<hr />
				
				<div class="p-field">
					<label for="newRequest">Seleziona una richiesta non pagata dell'atleta:</label>
					<Dropdown id="newRequest" v-model="newRequestId" :options="availableRequests" 
						optionLabel="label" optionValue="id" 
						placeholder="Seleziona richiesta..." class="p-inputtext-sm p-d-block" style="width:100%" />
				</div>

				<div v-if="selectedNewRequest" class="p-mt-2">
					<Message severity="info" :closable="false">
                        L'importo della richiesta selezionata è di {{ formatCurrency(selectedNewRequest.importo) }}.
                    </Message>
				</div>
			</div>
			
			<template #footer>
				<Button label="Annulla" icon="pi pi-times" class="p-button-text" @click="resolveDialog = false" />
				<Button label="Conferma Associazione" icon="pi pi-check" class="p-button-primary" 
					:disabled="!newRequestId" @click="confirmResolution" />
				<Button label="Crea e Associa Acconto" icon="pi pi-plus-circle" class="p-button-success" 
					@click="quickResolve" />
			</template>
		</Dialog>

        <Dialog v-model:visible="deleteDialog" header="Conferma Eliminazione" :style="{width: '450px'}" :modal="true">
            <div class="p-d-flex p-ai-center">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem; color: var(--red-500)" />
                <span v-if="selectedPayment">
                    Sei sicuro di voler eliminare l'<strong>intero pagamento</strong> di <strong>{{ formatCurrency(selectedPayment.importo_totale_pagamento) }}</strong>? 
                    Questa operazione cancellerà la transazione e tutti i suoi dettagli dal sistema.
                </span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text" @click="deleteDialog = false" />
                <Button label="Sì, elimina tutto" icon="pi pi-trash" class="p-button-danger" @click="executeDeletePayment" />
            </template>
        </Dialog>
	</div>
</template>

<script>
import Service from '@/services/levante-api/levante-api';

export default {
	data() {
		return {
			orphans: [],
			loading: false,
			resolveDialog: false,
			deleteDialog: false,
			selectedPayment: null,
			availableRequests: [],
			newRequestId: null,
			service: null
		}
	},
	created() {
		this.service = new Service(this.$eventBus);
		this.loadOrphans();
	},
	computed: {
		selectedNewRequest() {
			return this.availableRequests.find(r => r.id === this.newRequestId);
		}
	},
	methods: {
		async loadOrphans() {
			this.loading = true;
			try {
				const res = await this.service.getOrphanedPayments();
				this.orphans = res.items || [];
			} catch (error) {
				console.error(error);
			} finally {
				this.loading = false;
			}
		},
		async openResolveDialog(payment) {
			this.selectedPayment = payment;
			this.newRequestId = null;
			this.availableRequests = [];
			
			// Load unpaid requests for this athlete
			try {
				const res = await this.service.getAthleteUnpaidRequests(payment.id_atleta);
				this.availableRequests = (res.items || []).map(r => {
					let label = `${r.importo}€ - `;
					if (r.corso_nome) label += `Corso: ${r.corso_nome}`;
					else if (r.torneo_nome) label += `Torneo: ${r.torneo_nome}`;
					else if (r.prodotto_nome) label += `${r.prodotto_nome}`;
					else if (r.kit_nome) label += `Kit: ${r.kit_nome}`;
					else label += `Scritta: ${r.note}`;
					
					label += ` (Scad: ${r.data_scadenza})`;
					return { ...r, label };
				});
				this.resolveDialog = true;
			} catch (error) {
				console.error(error);
			}
		},
		async confirmResolution() {
			try {
				const res = await this.service.resolveOrphanedPayment(
					this.selectedPayment.id_pagamento_det, 
					this.newRequestId
				);
				
				if (res.item.esito === 'success') {
					this.$toast.add({severity:'success', summary: 'Risolto', detail:'Il pagamento è stato riassociato correttamente', life: 3000});
					this.resolveDialog = false;
					this.loadOrphans();
				} else {
					this.$toast.add({severity:'error', summary: 'Errore', detail: res.item.messaggio, life: 3000});
				}
			} catch (error) {
				console.error(error);
			}
		},
		async quickResolve() {
			try {
				this.loading = true;
				const res = await this.service.quickResolveWithAcconto(this.selectedPayment.id_pagamento_det);
				
				if (res.item.esito === 'success') {
					this.$toast.add({severity:'success', summary: 'Successo', detail: res.item.messaggio, life: 3000});
					this.resolveDialog = false;
					this.loadOrphans();
				} else {
					this.$toast.add({severity:'error', summary: 'Errore', detail: res.item.messaggio, life: 3000});
				}
			} catch (error) {
				console.error(error);
			} finally {
				this.loading = false;
			}
		},
        confirmDeletePayment(payment) {
            this.selectedPayment = payment;
            this.deleteDialog = true;
        },
        async executeDeletePayment() {
            try {
                const res = await this.service.deleteEntirePayment(this.selectedPayment.id_pagamento);
                if (res.item.esito === 'success') {
                    this.$toast.add({severity:'success', summary: 'Eliminato', detail: 'Pagamento rimosso dal sistema', life: 3000});
                    this.deleteDialog = false;
                    this.loadOrphans();
                } else {
                    this.$toast.add({severity:'error', summary: 'Errore', detail: res.item.messaggio, life: 3000});
                }
            } catch (error) {
                console.error(error);
            }
        },
		formatCurrency(value) {
			return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
		}
	}
}
</script>

<style scoped>
.p-field {
	margin-bottom: 1rem;
}
.atleta-link {
    color: var(--blue-600);
    text-decoration: none;
    font-weight: 600;
}
.atleta-link:hover {
    text-decoration: underline;
}
.text-bold {
    font-weight: bold;
    color: var(--primary-color);
}
.text-muted {
    color: #999;
    font-style: italic;
    font-size: 0.85rem;
}
.others-alert {
    color: var(--red-600);
    font-weight: 600;
    background: #fff5f5;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid #ffcccc;
}
.p-mr-2 {
    margin-right: 0.5rem;
}
.p-mr-3 {
    margin-right: 1rem;
}
.p-ml-1 {
    margin-left: 0.25rem;
}
.p-d-flex {
    display: flex;
}
.p-ai-center {
    align-items: center;
}
</style>
